// Editable fields match the existing HRMS update/create request DTOs.
export const DATE_OF_BIRTH_FIELDS = [
  { key: "dateOfBirth", label: "Date of birth", type: "date", cannotClear: true },
];

export const PHONE_FIELDS = [
  { key: "phone", label: "Phone", type: "tel", pattern: /^[0-9]{10,20}$/, formatMessage: "Enter a phone number containing 10 to 20 digits.", maxLength: 20, inputMode: "tel", cannotClear: true },
];

export const CONTACT_TYPE_FIELDS = [
  { key: "contactType", label: "Preferred contact", options: ["Email", "Phone", "Both"], cannotClear: true },
];

export const ADDRESS_FIELDS = [
  { key: "addressHouseNo", label: "House no.", maxLength: 50 },
  { key: "addressStreetName", label: "Street", maxLength: 50 },
  { key: "addressLocationArea", label: "Area / Location", maxLength: 50 },
  { key: "addressDistrict", label: "District", maxLength: 50 },
  { key: "addressState", label: "State", maxLength: 50 },
  { key: "addressPincode", label: "Pincode", pattern: /^[0-9]{6}$/, formatMessage: "Enter a 6-digit pincode.", maxLength: 6, inputMode: "numeric", cannotClear: true },
  { key: "addressDigipin", label: "Digipin", maxLength: 10 },
  { key: "addressGmapLink", label: "Google Maps link" },
];

export const EMERGENCY_FIELDS = [
  { key: "emergencyContactName", label: "Emergency contact name", maxLength: 150, requiredOnCreate: true },
  { key: "emergencyContact", label: "Emergency contact number", pattern: /^[0-9]{10,20}$/, formatMessage: "Enter a phone number containing 10 to 20 digits.", maxLength: 20, inputMode: "tel", requiredOnCreate: true, cannotClear: true },
  { key: "maritalStatus", label: "Marital status", options: ["Single", "Married", "Divorced", "Widowed", "Separated"], requiredOnCreate: true, cannotClear: true },
  { key: "bloodGroup", label: "Blood group", options: ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"], requiredOnCreate: true, cannotClear: true },
  { key: "fatherName", label: "Father's name", maxLength: 150 },
  { key: "motherName", label: "Mother's name", maxLength: 150 },
  { key: "noOfChildren", label: "Number of children", type: "number", min: 0, max: 20, cannotClear: true },
  { key: "healthReport", label: "Health report", type: "textarea", maxLength: 500, required: true },
  { key: "medicationInfo", label: "Medication details", type: "textarea", maxLength: 500, required: true },
];

export function profileDraft(fields, record) {
  return Object.fromEntries(fields.map(({ key, type }) => {
    const value = String(record?.[key] ?? "");
    return [key, type === "date" ? value.split("T")[0] : value];
  }));
}

export function yesterdayDate() {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return [date.getFullYear(), String(date.getMonth() + 1).padStart(2, "0"), String(date.getDate()).padStart(2, "0")].join("-");
}

// Send only changed editable fields. The API ignores null fields, and several
// optional fields reject empty strings, so unsupported clearing is explained.
export function buildProfileUpdate(fields, record, draft, creating = false) {
  const original = profileDraft(fields, record);
  const errors = {};
  const payload = {};
  for (const field of fields) {
    const value = String(draft[field.key] ?? "").trim();
    const previous = original[field.key].trim();
    const changed = value !== previous;
    const required = field.required || (creating && field.requiredOnCreate);
    if (!changed && !required && !creating) continue;
    if (!value && (required || (changed && previous && field.cannotClear))) {
      errors[field.key] = field.label + " is required.";
    } else if (value) {
      if (field.maxLength && value.length > field.maxLength) {
        errors[field.key] = "Use no more than " + field.maxLength + " characters.";
      } else if (field.pattern && !field.pattern.test(value)) {
        errors[field.key] = field.formatMessage;
      } else if (field.options && !field.options.includes(value)) {
        errors[field.key] = "Select a valid " + field.label.toLowerCase() + ".";
      } else if (field.type === "number" && (!Number.isInteger(Number(value)) || Number(value) < field.min || Number(value) > field.max)) {
        errors[field.key] = "Enter a whole number from " + field.min + " to " + field.max + ".";
      } else if (field.type === "date" && (
        !/^\d{4}-\d{2}-\d{2}$/.test(value) ||
        !Number.isFinite(Date.parse(value)) ||
        new Date(value).toISOString().slice(0, 10) !== value ||
        value > yesterdayDate()
      )) {
        errors[field.key] = "Enter a valid date of birth in the past.";
      }
    }
    if ((changed || required || creating) && (value || !field.cannotClear)) {
      payload[field.key] = field.type === "number" ? Number(value) : value;
    }
  }
  return { payload, errors };
}

// The employee-by-ID API returns the photo as base64 in empPic.
export function employeePhotoSource(master) {
  const raw = master?.empPicBase64 || master?.empPic || master?.photoBase64 || master?.photoUrl;
  if (typeof raw !== "string" || !raw.trim()) return "";
  const value = raw.trim();
  if (/^https?:\/\//i.test(value) || /^data:image\/(?:jpeg|png|gif|webp|bmp);base64,/i.test(value)) return value;
  const base64 = value.replace(/\s/g, "");
  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(base64)) return "";
  const formats = [["/9j/", "jpeg"], ["iVBORw0", "png"], ["R0lGOD", "gif"], ["UklGR", "webp"], ["Qk", "bmp"]];
  const format = formats.find(([prefix]) => base64.startsWith(prefix));
  return format ? "data:image/" + format[1] + ";base64," + base64 : "";
}
